import React, { useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { Device, DeviceLifecycleFields } from '../types';
import { formatTimestamp } from '../utils/formatTimestamp';
import Button from './ui/Button';

interface ClipInventoryBoxProps {
  device: Device;
  canWrite: boolean;
  /** Reload the device detail (after a success, or when the API says it changed). */
  onChanged?: () => void;
}

const MARK_ACTIVATED_TITLE =
  'Use only when a homeowner has set up this Clip but its activation never reached us. Uploads are stored from now on.';

/**
 * Settings tab: a Clip returned to inventory and not yet activated. Shows when
 * it was reset and what it has uploaded since (discarded), and offers Mark
 * activated for a Clip whose activation never reached us. No confirm dialog:
 * the operator decides; the API refuses (409) if the Clip left inventory or
 * was reset again since the detail was loaded.
 */
const ClipInventoryBox: React.FC<ClipInventoryBoxProps> = ({ device, canWrite, onChanged }) => {
  const [inFlight, setInFlight] = useState(false);
  // The error stays until the next click: after a 409 the reloaded detail may
  // show the Clip out of inventory, and the reason must still be visible.
  const [error, setError] = useState<string | null>(null);
  // A success belongs to the reset it was made against (the expected_reset_at
  // sent). The device detail reloads in place, so a later reset of the same
  // Clip must drop it and show the new cycle's button.
  const [marked, setMarked] = useState<{ resetAt: string | null | undefined; result: DeviceLifecycleFields } | null>(null);
  const markedThisCycle = marked && marked.resetAt === device.lifecycle_reset_at ? marked.result : null;

  const errorBox = error && (
    <div role="alert" className="mt-2 rounded-sm border border-status-error-border bg-red-100 p-2 text-red-700">{error}</div>
  );

  if (markedThisCycle) {
    return (
      <div role="status" className="mb-4 rounded-sm border border-status-success-border bg-green-50 p-3 text-sm text-green-800">
        Marked activated {formatTimestamp(markedThisCycle.activated_at)}. Uploads are stored from now on.
      </div>
    );
  }
  if (!device.is_in_inventory) {
    return error ? <div data-testid="clip-inventory-error" className="mb-4 text-sm">{errorBox}</div> : null;
  }

  const discarded = device.discarded_uploads;

  const markActivated = async () => {
    setInFlight(true);
    setError(null);
    try {
      const resetAt = device.lifecycle_reset_at;
      const res = await axiosInstance.post<DeviceLifecycleFields>(
        `${config.devicesApiUrl}/devices/${device.id}/mark-activated`,
        { expected_reset_at: resetAt, request_id: crypto.randomUUID() },
      );
      setMarked({ resetAt, result: res.data });
      onChanged?.();
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { message?: string } } };
      setError(e.response?.data?.message || 'Could not mark this Clip activated.');
      // 409: the Clip left inventory or was reset again; show the current state.
      if (e.response?.status === 409) onChanged?.();
    } finally {
      setInFlight(false);
    }
  };

  return (
    <div data-testid="clip-inventory" className="mb-4 rounded-sm border border-status-warning-border bg-amber-50 p-3 text-sm">
      <p className="font-medium text-amber-800">In inventory</p>
      <p className="mt-1 text-gray-700">
        Returned to inventory {formatTimestamp(device.lifecycle_reset_at)}. Not activated since.
      </p>
      <p className="mt-1 text-gray-700" title="Uploads received since the reset are discarded until the Clip is activated">
        {discarded && discarded.uploads > 0
          ? `Discarded since the reset: ${discarded.uploads} upload${discarded.uploads === 1 ? '' : 's'}, ${discarded.readings} reading${discarded.readings === 1 ? '' : 's'}, last received ${formatTimestamp(discarded.last_received_at)}.`
          : 'Nothing uploaded since the reset.'}
      </p>
      {errorBox}
      {canWrite && (
        <Button
          variant="primary"
          onClick={markActivated}
          disabled={inFlight}
          title={MARK_ACTIVATED_TITLE}
          className="mt-2"
        >
          {inFlight ? 'Marking activated…' : 'Mark activated'}
        </Button>
      )}
    </div>
  );
};

export default ClipInventoryBox;
